<template>
  <a-modal
    :visible="visible"
    :width="760"
    centered
    :closable="false"
    :mask-closable="false"
    :footer="null"
    wrap-class-name="patient-migration-notice-modal"
    @cancel="closeNotice"
  >
    <article class="migration-notice" aria-labelledby="migration-notice-title">
      <header class="migration-notice__header">
        <span class="migration-notice__eyebrow">
          {{ language === 'id' ? 'Pemberitahuan Penting' : 'Important Notice' }}
        </span>
        <h2 id="migration-notice-title">
          {{
            language === 'id'
              ? 'Migrasi Patient Affordability Program (PAP) ke PULIH 2.0'
              : 'Migration of the Patient Affordability Program (PAP) to PULIH 2.0'
          }}
        </h2>
        <span class="migration-notice__program">Program {{ normalizedProgramName }}</span>
      </header>

      <div v-if="hasEnglishVersion" class="migration-notice__language" aria-label="Pilihan bahasa">
        <button
          type="button"
          :class="{ active: language === 'id' }"
          @click="language = 'id'"
        >
          Bahasa Indonesia
        </button>
        <button
          type="button"
          :class="{ active: language === 'en' }"
          @click="language = 'en'"
        >
          English
        </button>
      </div>

      <div class="migration-notice__body">
        <template v-if="language === 'id'">
          <div class="migration-notice__note">
            <strong>Catatan:</strong> PULIH adalah aplikasi saat ini dan PULIH 2.0 adalah aplikasi
            baru.
          </div>

          <p>Yth. Bapak/Ibu,</p>
          <p>
            Sebagai bagian dari upaya kami untuk meningkatkan kualitas layanan Patient
            Affordability Program (PAP), kami akan memigrasikan layanan PAP ke aplikasi PULIH 2.0.
          </p>
          <p>
            Sebagai bagian dari proses ini, data pribadi Anda yang saat ini digunakan untuk
            administrasi PAP akan dipindahkan ke sistem PULIH 2.0, yang dioperasikan oleh mitra
            resmi kami, IQVIA, untuk mendukung pelaksanaan program.
          </p>

          <section>
            <h3>Apa arti perubahan ini bagi Anda?</h3>
            <ul>
              <li>Data pribadi Anda akan dipindahkan secara aman ke sistem PULIH 2.0.</li>
              <li>
                Tujuan penggunaan data pribadi Anda tidak berubah. Data Anda akan tetap digunakan
                semata-mata untuk pelaksanaan dan administrasi PAP.
              </li>
              <li>
                Untuk siklus terapi Anda berikutnya, Anda akan dihubungi oleh IQVIA, sebagai mitra
                bisnis AstraZeneca Indonesia, dan dipandu untuk mengunduh serta menggunakan
                aplikasi PULIH 2.0 melalui Google Play Store atau Apple App Store, lalu melanjutkan
                proses seperti biasa.
              </li>
              <li>
                Setelah itu, Anda dapat melanjutkan program dan transaksi selanjutnya melalui
                aplikasi PULIH 2.0.
              </li>
            </ul>
          </section>

          <section>
            <h3>Jadwal Migrasi Aplikasi</h3>
            <dl class="migration-notice__schedule">
              <div>
                <dt>{{ schedule.extension }}</dt>
                <dd>Batas akhir perpanjangan untuk penukaran terakhir di aplikasi PULIH</dd>
              </div>
              <div>
                <dt>{{ schedule.invitation }}</dt>
                <dd>Undangan untuk aktivasi dan penggunaan aplikasi PULIH 2.0 akan dikirimkan</dd>
              </div>
              <div>
                <dt>{{ schedule.redemption }}</dt>
                <dd>Penukaran terakhir di PULIH</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>Privasi dan Keamanan Data Anda</h3>
            <p>
              AstraZeneca Indonesia berkomitmen untuk melindungi data pribadi Anda dan menghormati
              hak Anda sebagai subjek data pribadi. Pemrosesan, penggunaan, penyimpanan,
              pengungkapan, dan pemindahan data pribadi Anda sehubungan dengan administrasi Program
              Keterjangkauan Pasien (PAP) dilakukan sesuai dengan Undang-Undang Nomor 27 Tahun 2022
              tentang Pelindungan Data Pribadi dan peraturan perundang-undangan lain yang berlaku.
            </p>
            <p>Data pribadi yang diproses dapat meliputi:</p>
            <ul class="migration-notice__data-list">
              <li>Nama pasien</li>
              <li>Nomor Induk Kependudukan</li>
              <li>Tempat dan tanggal lahir</li>
              <li>Jenis kelamin</li>
              <li>Kota pasien</li>
              <li>Foto kartu identitas (KTP/KITAS)</li>
              <li>Foto swafoto</li>
              <li>Nama pendamping/perwakilan pasien</li>
              <li>Informasi kontak pasien dan kontak pendamping/perwakilan pasien</li>
              <li>Informasi tenaga kesehatan yang merawat (HCP)</li>
              <li>Informasi terkait terapi (resep dokter dan hasil pemeriksaan laboratorium)</li>
              <li>Foto bukti transaksi pasien</li>
              <li>Data lain yang diperlukan untuk administrasi PAP</li>
            </ul>
            <p>
              Data tersebut hanya akan diakses oleh AstraZeneca Indonesia dan afiliasinya serta
              penyedia layanan resmi yang ditunjuk untuk tujuan administrasi program, layanan
              pasien, pemantauan keamanan obat (farmakovigilans/patient safety), dan pemenuhan
              kewajiban hukum yang berlaku.
            </p>
            <p>
              Dengan tunduk pada hukum yang berlaku, saya memahami bahwa kelanjutan keikutsertaan
              saya dalam PAP setelah pemberitahuan ini akan dianggap sebagai persetujuan saya atas
              pemrosesan dan pemindahan data pribadi saya ke sistem PULIH 2.0 untuk administrasi
              PAP, serta persetujuan untuk dihubungi dan diproses data pribadinya oleh AstraZeneca
              Indonesia dan/atau penyedia layanan resmi yang ditunjuk, termasuk IQVIA, sehubungan
              dengan pelaksanaan program, layanan pasien, pemantauan keamanan obat
              (farmakovigilans/patient safety), dan pemenuhan kewajiban berdasarkan peraturan
              perundang-undangan yang berlaku.
            </p>
          </section>

          <section>
            <h3>Informasi Kontak</h3>
            <p>Jika Anda memiliki pertanyaan mengenai migrasi ini, silakan hubungi:</p>
            <address class="migration-notice__contact">
              <strong>{{ contactLabel }}:</strong>
              <a :href="contactHref">{{ contactNumber }}</a><br />
              <strong>Email IQVIA:</strong>
              <a href="mailto:pulih2customer.support@psplink.com">
                pulih2customer.support@psplink.com
              </a><br />
              <strong>Jam Operasional:</strong> Senin–Jumat, 08.00–16.00
            </address>
          </section>

          <p>
            Terima kasih atas kepercayaan Anda kepada kami. Kami berkomitmen untuk terus memberikan
            layanan yang aman, nyaman, dan berkelanjutan bagi Anda.
          </p>
        </template>

        <template v-else>
          <div class="migration-notice__note">
            <strong>Notes:</strong> PULIH is the current application and PULIH 2.0 is the new
            application.
          </div>

          <p>Dear Sir/Madam,</p>
          <p>
            As part of our efforts to improve the quality of the Patient Affordability Program
            (PAP) services, we will be migrating PAP services to the PULIH 2.0 application.
          </p>
          <p>
            As part of this process, your personal data currently used for the administration of
            PAP will be transferred to the PULIH 2.0 system, which is operated by our official
            partner, IQVIA, to support the implementation of the program.
          </p>

          <section>
            <h3>What does this change mean for you?</h3>
            <ul>
              <li>Your personal data will be securely transferred to the PULIH 2.0 system.</li>
              <li>
                The purpose for using your personal data remains unchanged. Your data will continue
                to be used solely for the implementation and administration of PAP.
              </li>
              <li>
                For your next therapy cycle, you will be contacted by IQVIA, as AstraZeneca
                Indonesia's business partner, and guided to download and use the PULIH 2.0
                application through the Google Play Store or Apple App Store, and to continue the
                process as usual.
              </li>
              <li>
                After that, you may continue the program and subsequent transactions through the
                PULIH 2.0 application.
              </li>
            </ul>
          </section>

          <section>
            <h3>Application Migration Schedule</h3>
            <dl class="migration-notice__schedule">
              <div>
                <dt>30 August 2026</dt>
                <dd>Deadline for extension for the last redemption in the PULIH application</dd>
              </div>
              <div>
                <dt>31 August 2026</dt>
                <dd>Invitation for activation and use of the PULIH 2.0 application will be sent</dd>
              </div>
              <div>
                <dt>14 September 2026</dt>
                <dd>Last redemption in PULIH</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>Your Data Privacy and Security</h3>
            <p>
              AstraZeneca Indonesia is committed to protecting your personal data and respecting
              your rights as a personal data subject. The processing, use, storage, disclosure, and
              transfer of your personal data in connection with the administration of the Patient
              Affordability Program (PAP) are carried out in accordance with Law Number 27 of 2022
              on Personal Data Protection and other applicable laws and regulations.
            </p>
            <p>The personal data processed may include:</p>
            <ul class="migration-notice__data-list">
              <li>Patient name</li>
              <li>National Identification Number</li>
              <li>Place and date of birth</li>
              <li>Gender</li>
              <li>Patient's city</li>
              <li>Photo of identity card (KTP/KITAS)</li>
              <li>Selfie photo</li>
              <li>Patient representative's name</li>
              <li>Patient contact information and patient representative contact information</li>
              <li>Information on the treating healthcare professional (HCP)</li>
              <li>
                Therapy-related information (doctor's prescription and laboratory test results)
              </li>
              <li>Photo of the patient's proof of transaction</li>
              <li>Other data required for the administration of PAP</li>
            </ul>
            <p>
              Such data will only be accessed by AstraZeneca Indonesia and its affiliates and
              appointed official service providers for the purposes of program administration,
              patient services, drug safety monitoring (pharmacovigilance/patient safety), and
              compliance with applicable legal obligations.
            </p>
            <p>
              Subject to applicable laws, I understand that my continuation in the PAP following
              this notification shall constitute my deemed consent to the processing and transfer
              of my personal data to the PULIH 2.0 system for the administration of the PAP, and
              consent to being contacted and my personal data being processed by AstraZeneca
              Indonesia and/or its appointed official service providers, including IQVIA, in
              relation to the implementation of the program, patient services, drug safety
              monitoring (pharmacovigilance/patient safety), and compliance with obligations under
              applicable laws and regulations.
            </p>
          </section>

          <section>
            <h3>Contact Information</h3>
            <p>If you have any questions regarding this migration, please contact:</p>
            <address class="migration-notice__contact">
              <strong>IQVIA Customer Services:</strong>
              <a href="tel:+622150851077">(021-5085-1077)</a><br />
              <strong>IQVIA email:</strong>
              <a href="mailto:pulih2customer.support@psplink.com">
                pulih2customer.support@psplink.com
              </a><br />
              <strong>Operating Hours:</strong> Monday–Friday, 08:00–16:00
            </address>
          </section>

          <p>
            Thank you for your trust in us. We are committed to continuing to provide services that
            are safe, convenient, and sustainable for you.
          </p>
        </template>
      </div>

      <footer class="migration-notice__footer">
        <a-button type="primary" size="large" block @click="closeNotice">
          {{ language === 'id' ? 'Saya Mengerti' : 'I Understand' }}
        </a-button>
      </footer>
    </article>
  </a-modal>
</template>

<script>
import { computed, ref, watch } from 'vue'

const FIRST_WAVE_PROGRAMS = ['KOSELUGO', 'IMJUDO', 'FASENRA', 'TEZPIRE', 'IMFINZI']

export default {
  name: 'PatientMigrationNotice',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    programName: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const language = ref('id')
    const normalizedProgramName = computed(() => props.programName.trim().toUpperCase())
    const isFirstWave = computed(() => FIRST_WAVE_PROGRAMS.includes(normalizedProgramName.value))
    const hasEnglishVersion = computed(() => !isFirstWave.value)
    const schedule = computed(() =>
      isFirstWave.value
        ? {
            extension: '6 Agustus 2026',
            invitation: '7 Agustus 2026',
            redemption: '31 Agustus 2026',
          }
        : {
            extension: '30 Agustus 2026',
            invitation: '31 Agustus 2026',
            redemption: '14 September 2026',
          },
    )
    const contactLabel = computed(() =>
      isFirstWave.value ? 'Customer Service IQVIA' : 'Layanan Pelanggan IQVIA',
    )
    const contactNumber = computed(() =>
      isFirstWave.value ? '+6281574584552' : '(021-5085-1077)',
    )
    const contactHref = computed(() =>
      isFirstWave.value ? 'tel:+6281574584552' : 'tel:+622150851077',
    )

    watch(
      () => props.visible,
      visible => {
        if (visible) language.value = 'id'
      },
    )

    const closeNotice = () => emit('close')

    return {
      contactHref,
      contactLabel,
      contactNumber,
      closeNotice,
      hasEnglishVersion,
      language,
      normalizedProgramName,
      schedule,
    }
  },
}
</script>

<style lang="scss" scoped>
.migration-notice {
  color: #3f3542;

  &__header {
    padding: 4px 8px 18px;
    border-bottom: 1px solid #eee3ec;

    h2 {
      margin: 7px 0 10px;
      color: #4f125f;
      font-size: 24px;
      font-weight: 800;
      line-height: 1.25;
    }
  }

  &__eyebrow {
    color: #800152;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__program {
    display: inline-flex;
    padding: 4px 10px;
    color: #ffffff;
    background: linear-gradient(135deg, #800152 0%, #4f125f 100%);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
  }

  &__language {
    display: flex;
    gap: 8px;
    padding: 16px 8px 0;

    button {
      padding: 7px 13px;
      color: #6b5b68;
      background: #ffffff;
      border: 1px solid #d8ccd5;
      border-radius: 8px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;

      &.active {
        color: #ffffff;
        background: #800152;
        border-color: #800152;
      }
    }
  }

  &__body {
    max-height: 58vh;
    padding: 18px 8px 8px;
    overflow-y: auto;
    overscroll-behavior: contain;
    font-size: 14px;
    line-height: 1.65;

    p {
      margin: 0 0 14px;
    }

    section {
      margin-top: 22px;
    }

    h3 {
      margin: 0 0 10px;
      color: #4f125f;
      font-size: 17px;
      font-weight: 800;
    }

    ul {
      margin: 0 0 14px;
      padding-left: 21px;
    }

    li + li {
      margin-top: 6px;
    }
  }

  &__note {
    margin-bottom: 18px;
    padding: 12px 14px;
    color: #5d294c;
    background: #fbf1f7;
    border-left: 4px solid #800152;
    border-radius: 8px;
  }

  &__schedule {
    margin: 0;

    div {
      display: grid;
      grid-template-columns: 145px 1fr;
      gap: 14px;
      padding: 11px 12px;
      border-bottom: 1px solid #eee3ec;

      &:first-child {
        border-top: 1px solid #eee3ec;
      }
    }

    dt {
      color: #800152;
      font-weight: 800;
    }

    dd {
      margin: 0;
    }
  }

  &__data-list {
    columns: 2;
    column-gap: 32px;

    li {
      break-inside: avoid;
    }
  }

  &__contact {
    margin-bottom: 18px;
    padding: 14px 16px;
    background: #f8f7f8;
    border-radius: 10px;
    font-style: normal;

    a {
      color: #800152;
      overflow-wrap: anywhere;
    }
  }

  &__footer {
    display: flex;
    padding: 16px 8px 2px;
    border-top: 1px solid #eee3ec;

    :deep(.ant-btn-primary) {
      height: 58px;
      background: #4f125f;
      border-color: #4f125f;
      border-radius: 12px;
      box-shadow: 0 6px 14px rgba(79, 18, 95, 0.2);
      font-size: 18px;
      font-weight: 700;
      transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;

      &:hover,
      &:focus {
        background: #661773;
        border-color: #661773;
        box-shadow: 0 7px 18px rgba(79, 18, 95, 0.28);
      }

      &:focus-visible {
        outline: 3px solid rgba(79, 18, 95, 0.28);
        outline-offset: 3px;
      }
    }
  }
}

@media (max-width: 575px) {
  .migration-notice {
    &__header {
      padding-right: 0;
      padding-left: 0;

      h2 {
        font-size: 20px;
      }
    }

    &__language,
    &__body,
    &__footer {
      padding-right: 0;
      padding-left: 0;
    }

    &__body {
      max-height: 61vh;
      font-size: 13px;
    }

    &__schedule div {
      display: block;

      dt {
        margin-bottom: 3px;
      }
    }

    &__data-list {
      columns: 1;
    }

    &__footer :deep(.ant-btn-primary) {
      height: 52px;
      width: 100%;
      font-size: 16px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .migration-notice__footer :deep(.ant-btn-primary) {
    transition: none;
  }
}
</style>
